// Thin WebRTC adapter between the advisor softphone controller and a telephony SDK (plan B3).
//
// LIA places and bridges every call with Telnyx Call Control; the browser is only the advisor's audio leg. So the
// adapter only needs to: register (connect with a short-lived login token), answer/hang up the invite LIA (or a ring
// group) sends to this browser, and local audio controls (mute, hold, DTMF). The real implementation is
// telnyx-adapter.ts; tests use fake-adapter.ts.

/** SDK call state reduced to what the UI needs. */
export type AdapterCallState = 'ringing' | 'active' | 'held' | 'hangup';

export type SoftphoneAdapterEvent =
  /** Connected and registered: calls can reach this browser. */
  | { type: 'ready' }
  /** The SDK reported an error (message is Spanish, shown as is). */
  | { type: 'error'; message: string }
  /** The connection to the provider closed (reconnect fetches a new token). */
  | { type: 'disconnected' }
  /** An inbound invite reached this browser (LIA's own outbound leg or a ring-group call). */
  | { type: 'incoming'; callId: string; remoteNumber: string | null }
  /** State change of the current SDK call. */
  | { type: 'call'; callId: string; state: AdapterCallState };

export type SoftphoneAdapterListener = (event: SoftphoneAdapterEvent) => void;

export interface SoftphoneAdapter {
  connect(loginToken: string): Promise<void>;
  disconnect(): Promise<void>;
  /** Answer the pending invite (no-op without one). */
  answer(): Promise<void>;
  /** Hang up / reject the current call or pending invite (no-op without one). */
  hangup(): Promise<void>;
  setMuted(muted: boolean): void;
  setHeld(held: boolean): Promise<void>;
  sendDtmf(digit: string): void;
  subscribe(listener: SoftphoneAdapterListener): () => void;
}

export type GetUserMedia = (constraints: MediaStreamConstraints) => Promise<MediaStream>;

export const MIC_MESSAGES = {
  denied: 'No hay permiso para usar el micrófono. Permítelo en la configuración del navegador y vuelve a intentar.',
  notFound: 'No se encontró un micrófono conectado.',
  unsupported: 'Este navegador no permite llamadas desde la página (se requiere HTTPS y un navegador compatible).',
  other: 'No se pudo abrir el micrófono.',
} as const;

/** Maps a getUserMedia failure to the Spanish message shown to the advisor. */
export function micErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return MIC_MESSAGES.denied;
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return MIC_MESSAGES.notFound;
  return MIC_MESSAGES.other;
}

/** The browser's getUserMedia, or null when unavailable (no mediaDevices, insecure context). */
export function browserGetUserMedia(): GetUserMedia | null {
  if (typeof window === 'undefined' || window.isSecureContext === false) return null;
  const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!media?.getUserMedia) return null;
  return (c) => media.getUserMedia(c);
}

/**
 * Asks for the microphone (so the browser prompt appears on the advisor's click, before the SDK needs it) and releases
 * the stream right away: the SDK opens its own when it answers.
 */
export async function requestMicrophone(getUserMedia: GetUserMedia | null): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!getUserMedia) return { ok: false, message: MIC_MESSAGES.unsupported };
  try {
    const stream = await getUserMedia({ audio: true });
    stream?.getTracks?.().forEach((t) => t.stop());
    return { ok: true };
  } catch (err) {
    return { ok: false, message: micErrorMessage(err) };
  }
}
